import React from 'react';
import { Check, Flame, Info, LogOut, Moon, RotateCw, Settings as SettingsIcon, Sparkles, ThumbsDown, ThumbsUp, Zap } from 'lucide-react';
import { CoachResponse, DailyStats, Race, User, WorkoutRecommendation } from '../types';
import { ServiceConnection } from './ServiceConnection';
import { StatsCard } from './StatsCard';
import { CoachChat } from './CoachChat';
import { CheckIn } from './CheckIn';
import { LogActivity } from './LogActivity';
import { PlanHistory } from './PlanHistory';
import { RaceStrip } from './RaceStrip';
import { Logo } from './Logo';
import { CoachContext } from '../services/aiCoach';
import { CheckInFeeling, DailyCheckIn } from '../services/checkIn';
import { useStats } from '../hooks/useStats';
import { signOut } from '../services/auth';

interface DashboardProps {
  user: User;
  recommendation: WorkoutRecommendation | null;
  recommendationSource: 'ai' | 'rules';
  races: Race[];
  coachContext: CoachContext | null;
  coachIntro: string | null;
  checkIn: DailyCheckIn | null;
  awaitingCheckIn: boolean;
  onCheckIn: (feeling: CheckInFeeling, note: string | null) => void;
  onSkipCheckIn: () => void;
  todayRating: 'up' | 'down' | null;
  todayCompleted: boolean | null;
  onRate: (rating: 'up' | 'down' | null) => void;
  onMarkCompleted: (completed: boolean | null) => void;
  onCoachUpdate: (coach: CoachResponse) => void;
  onRegenerate: () => void;
  regenerating: boolean;
  onSettingsClick: () => void;
  /** Demo mode: fictional sample day, no account, no persistence. */
  demo?: boolean;
  demoStats?: DailyStats;
  onExitDemo?: () => void;
  onSignUp?: () => void;
}

const FEELING_EMOJI: Record<CheckInFeeling, string> = {
  rough: '😫',
  okay: '😐',
  strong: '💪',
};

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

export function Dashboard({
  user,
  recommendation,
  recommendationSource,
  races,
  coachContext,
  coachIntro,
  checkIn,
  awaitingCheckIn,
  onCheckIn,
  onSkipCheckIn,
  todayRating,
  todayCompleted,
  onRate,
  onMarkCompleted,
  onCoachUpdate,
  onRegenerate,
  regenerating,
  onSettingsClick,
  demo = false,
  demoStats,
  onExitDemo,
  onSignUp,
}: DashboardProps) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const live = useStats();
  const stats = demo ? demoStats : live.stats;
  const isLoading = demo ? false : live.isLoading;
  const hasOuraData = demo || live.hasOuraData;
  const hasStravaData = demo || live.hasStravaData;

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getReadinessStatus = () => {
    if (!stats?.oura?.recovery) {
      return { status: 'No data yet', classes: 'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400', dot: 'bg-zinc-400' };
    }

    const { hrv, restingHeartRate, readinessScore } = stats.oura.recovery;
    const { quality: sleepQuality } = stats.oura.sleep;

    // Calculate readiness score based on available metrics
    const score = (
      (hrv / 70 * 0.4) +
      (sleepQuality / 100 * 0.4) +
      ((70 - restingHeartRate) / 30 * 0.2)
    );

    const ready = { status: 'Ready to train', classes: 'bg-emerald-600/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300', dot: 'bg-emerald-500' };
    const light = { status: 'Light training OK', classes: 'bg-amber-600/10 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300', dot: 'bg-amber-500' };
    const recover = { status: 'Recovery recommended', classes: 'bg-rose-600/10 text-rose-700 dark:bg-rose-400/10 dark:text-rose-300', dot: 'bg-rose-500' };

    if (readinessScore !== undefined) {
      // If Oura provides a readiness score, use that
      if (readinessScore >= 85) return ready;
      if (readinessScore >= 70) return light;
      return recover;
    }

    // Fallback to calculated score
    if (score >= 0.8) return ready;
    if (score >= 0.6) return light;
    return recover;
  };

  const readinessStatus = getReadinessStatus();

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-zinc-950/5 bg-zinc-50/80 backdrop-blur dark:border-white/5 dark:bg-zinc-950/80">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3.5">
          <Logo />
          {demo ? (
            <div className="flex items-center gap-2">
              <button onClick={onExitDemo} className="btn-ghost">
                Exit demo
              </button>
              <button onClick={onSignUp} className="btn-primary">
                Sign up free
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <button onClick={onSettingsClick} className="btn-ghost px-2.5" aria-label="Settings">
                <SettingsIcon className="h-5 w-5" />
              </button>
              <button onClick={handleSignOut} className="btn-ghost px-2.5" aria-label="Sign out">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </header>

      {demo && (
        <div className="border-b border-indigo-600/10 bg-indigo-600/5 dark:border-indigo-400/10 dark:bg-indigo-400/5">
          <p className="mx-auto max-w-5xl px-6 py-2 text-center text-xs font-medium text-indigo-700 dark:text-indigo-300">
            You&apos;re exploring a sample day — the athlete and data are fictional, the coach is live.
            Everything here works: check in, chat, regenerate.
          </p>
        </div>
      )}

      <main className="mx-auto max-w-5xl px-6 py-8">
        {/* Greeting */}
        <div className="mb-8 animate-fade-up">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                {greeting()}, {user.name.split(' ')[0] || 'athlete'}
              </h1>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <span className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold ${readinessStatus.classes}`}>
              <span className={`h-2 w-2 rounded-full ${readinessStatus.dot}`} />
              {readinessStatus.status}
            </span>
          </div>
          {races.length > 0 && (
            <div className="mt-5">
              <RaceStrip races={races} />
            </div>
          )}
        </div>

        {/* Feel-first check-in — asked once, before the day's plan exists */}
        {!recommendation && awaitingCheckIn && (
          <div className="mb-6 animate-fade-up-delay-1">
            <CheckIn onSelect={onCheckIn} onSkip={onSkipCheckIn} />
          </div>
        )}
        {!recommendation && !awaitingCheckIn && regenerating && (
          <div className="card mb-6 flex items-center gap-3 p-6 animate-fade-up-delay-1">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-200 border-t-indigo-600 dark:border-zinc-700 dark:border-t-indigo-400" />
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Got it — building today&apos;s plan…
            </p>
          </div>
        )}

        {/* Recommendation — the product leads; metrics follow */}
        {recommendation && (
          <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-5 animate-fade-up-delay-1">
            {/* Today's workout */}
            <div className="card relative overflow-hidden p-6 lg:col-span-3">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-indigo-500/15 to-violet-500/15 blur-2xl" />
              <div className="flex items-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                <Zap className="h-4 w-4" />
                Today&apos;s workout
                {recommendationSource === 'ai' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600/10 px-2 py-0.5 text-[11px] font-semibold dark:bg-indigo-400/10">
                    <Sparkles className="h-3 w-3" />
                    AI coach
                  </span>
                )}
                {checkIn && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                    title={`Your check-in: feeling ${checkIn.feeling}${checkIn.note ? ` — “${checkIn.note}”` : ''}`}
                  >
                    {FEELING_EMOJI[checkIn.feeling]} You: {checkIn.feeling}
                  </span>
                )}
                <button
                  onClick={onRegenerate}
                  disabled={regenerating}
                  className="ml-auto text-zinc-400 transition hover:text-zinc-600 disabled:opacity-50 dark:hover:text-zinc-300"
                  title="Regenerate today's plan"
                  aria-label="Regenerate today's plan"
                >
                  <RotateCw className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
                {recommendation.type}
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {recommendation.duration} min
                </span>
                <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-semibold capitalize text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {recommendation.intensity} intensity
                </span>
              </div>
              <p className="mt-4 leading-relaxed text-zinc-600 dark:text-zinc-400">
                {recommendation.details}
              </p>

              <div className="mt-6 border-t border-zinc-950/5 pt-5 dark:border-white/5">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Why this workout?</h3>
                  <button
                    className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    aria-label="How readiness is calculated"
                  >
                    <Info className="h-4 w-4" />
                  </button>
                </div>
                {showTooltip && stats?.oura && (
                  <div className="mt-3 rounded-xl bg-zinc-900 p-4 text-sm text-zinc-200 dark:bg-zinc-800">
                    Training readiness weighs:
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-zinc-300">
                      <li>HRV: {stats.oura.recovery.hrv}ms — {stats.oura.recovery.hrv >= 35 ? 'good' : 'below average'}</li>
                      <li>Sleep quality: {stats.oura.sleep.quality}% — {stats.oura.sleep.quality >= 75 ? 'good' : 'below average'}</li>
                      <li>Resting heart rate: {stats.oura.recovery.restingHeartRate} bpm — {stats.oura.recovery.restingHeartRate <= 55 ? 'good' : 'elevated'}</li>
                    </ul>
                  </div>
                )}
                <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                  {recommendation.reasoning}
                </p>
              </div>

              {/* Feedback — completion + rating feed tomorrow's coach context */}
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-950/5 pt-4 dark:border-white/5">
                <button
                  onClick={() => onMarkCompleted(todayCompleted === true ? null : true)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ring-1 transition ${
                    todayCompleted === true
                      ? 'bg-emerald-600/10 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20'
                      : 'bg-zinc-50 text-zinc-600 ring-zinc-950/5 hover:bg-zinc-100 dark:bg-zinc-800/60 dark:text-zinc-300 dark:ring-white/5 dark:hover:bg-zinc-800'
                  }`}
                >
                  <Check className="h-4 w-4" />
                  {todayCompleted === true ? 'Done — nice work' : 'Mark as done'}
                </button>
                <div className="flex items-center gap-1">
                  <span className="mr-1 text-xs text-zinc-400">Right call?</span>
                  <button
                    onClick={() => onRate(todayRating === 'up' ? null : 'up')}
                    className={`rounded-lg p-1.5 transition ${
                      todayRating === 'up'
                        ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400'
                        : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                    }`}
                    aria-label="Good plan"
                  >
                    <ThumbsUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onRate(todayRating === 'down' ? null : 'down')}
                    className={`rounded-lg p-1.5 transition ${
                      todayRating === 'down'
                        ? 'bg-rose-600/10 text-rose-600 dark:bg-rose-400/10 dark:text-rose-400'
                        : 'text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300'
                    }`}
                    aria-label="Not for me"
                  >
                    <ThumbsDown className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Alternatives + coach */}
            <div className="space-y-6 lg:col-span-2">
              <div className="card p-6">
                <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Feeling different today?
                </h3>
                <div className="mt-4 space-y-3">
                  <div className="rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-violet-600 dark:text-violet-400">
                      <Moon className="h-4 w-4" />
                      Low energy
                    </div>
                    <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">
                      {recommendation.alternativeOptions.lowEnergy}
                    </p>
                  </div>
                  <div className="rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                      <Flame className="h-4 w-4" />
                      High energy
                    </div>
                    <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-400">
                      {recommendation.alternativeOptions.highEnergy}
                    </p>
                  </div>
                </div>
              </div>

              {recommendationSource === 'ai' && coachContext && (
                <CoachChat
                  context={coachContext}
                  intro={coachIntro}
                  onCoachUpdate={onCoachUpdate}
                  demo={demo}
                />
              )}
            </div>
          </div>
        )}

        {/* Wearable metrics + manual logging */}
        {isLoading ? (
          <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="card h-64 animate-pulse" />
            <div className="card h-64 animate-pulse" />
          </div>
        ) : stats && (stats.oura || stats.strava) ? (
          <div className="mb-6 animate-fade-up-delay-2">
            <StatsCard stats={stats} />
          </div>
        ) : null}

        {!demo && (
          <div className="mb-6 animate-fade-up-delay-2">
            <LogActivity />
          </div>
        )}

        {!demo && <PlanHistory />}

        {/* Connect services */}
        {(!hasOuraData || !hasStravaData) && (
          <div className="animate-fade-up-delay-2">
            <ServiceConnection user={user} />
          </div>
        )}
      </main>
    </div>
  );
}
